// PWA install prompt hook. Captures the `beforeinstallprompt` event once, and
// exposes { canInstall, install() } so any header can show a subtle button.
// Silently returns { canInstall: false } on iOS Safari and inside an already-installed
// standalone context.

import { useEffect, useState, useCallback } from "react";

export function usePwaInstall() {
  const [prompt, setPrompt] = useState(null);
  const [installed, setInstalled] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
  });

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setPrompt(e); };
    const onInstalled = () => { setInstalled(true); setPrompt(null); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    if (!prompt) return null;
    prompt.prompt();
    const choice = await prompt.userChoice;
    setPrompt(null);
    return choice.outcome; // "accepted" | "dismissed"
  }, [prompt]);

  return { canInstall: !!prompt && !installed, installed, install };
}
